/**
 * @flow
 * @file i18n messages
 * @author Box
 */

import { defineMessages } from 'react-intl';

const messages = defineMessages({
    taskCollaboratorLoadErrorMessage: {
        id: 'be.contentSidebar.activityFeed.taskNew.taskCollaboratorLoadErrorMessage',
        description: 'Error message when we failed to load the collaborators when user tries to edit a task',
        defaultMessage: 'An error has occurred while loading collaborators for this task. Please try again.',
    },
    taskShowMoreAssignees: {
        id: 'be.contentSidebar.activityFeed.taskNew.taskShowMoreAssignees',
        description:
            'Button name to expand task assignee list, additionalAssigneeCount is the number of additional task assignees that can be shown.',
        defaultMessage: 'Show {additionalAssigneeCount} More',
    },
    taskShowMoreAssigneesOverflow: {
        id: 'be.contentSidebar.activityFeed.taskNew.taskShowMoreAssigneesOverflow',
        description:
            'Button name to expand task assignee list, when there is an unknown number of assignees beyond additionalAssigneeCount.',
        defaultMessage: '{additionalAssigneeCount, plural, one {Show #+ More} other {Show #+ More}}',
    },
    taskShowLessAssignees: {
        id: 'be.contentSidebar.activityFeed.taskNew.taskShowLessAssignees',
        description: 'Button name to hide task assignee list',
        defaultMessage: 'Show Less',
    },
    taskDueDateLabel: {
        id: 'be.contentSidebar.activityFeed.taskNew.taskDueDateLabel',
        defaultMessage: 'Due: {date}',
        description: 'Label and date for task due date',
    },
    tasksFeedApproveAction: {
        id: 'be.contentSidebar.activityFeed.taskNew.tasksFeedApproveAction',
        defaultMessage: 'Approve',
        description: 'Approve option for an approval task',
    },
    tasksFeedCompleteAction: {
        id: 'be.contentSidebar.activityFeed.taskNew.tasksFeedCompleteAction',
        defaultMessage: 'Mark as Complete',
        description: 'Completion option for a general task',
    },
    tasksFeedRejectAction: {
        id: 'be.contentSidebar.activityFeed.taskNew.tasksFeedRejectAction',
        defaultMessage: 'Reject',
        description: 'Reject option for an approval task',
    },
    tasksFeedViewDetailsAction: {
        id: 'be.contentSidebar.activityFeed.taskNew.tasksFeedViewDetailsAction',
        defaultMessage: 'View Task Details',
        description: 'View the details for a task',
    },
    tasksFeedCompletedLabel: {
        id: 'be.contentSidebar.activityFeed.taskNew.tasksFeedCompletedLabel',
        defaultMessage: 'Completed',
        description: 'Label for a completed task',
    },
    taskFeedCompletedUppercaseLabel: {
        id: 'be.contentSidebar.activityFeed.taskNew.tasksFeedCompletedUppercaseLabel',
        defaultMessage: 'COMPLETED',
        description: 'Label for an completed task (in upper-case in supported language)',
    },
    tasksFeedApprovedLabel: {
        id: 'be.contentSidebar.activityFeed.taskNew.tasksFeedApprovedLabel',
        defaultMessage: 'Approved',
        description: 'Label for an approved task',
    },
    taskFeedApprovedUppercaseLabel: {
        id: 'be.contentSidebar.activityFeed.taskNew.tasksFeedApprovedUppercaseLabel',
        defaultMessage: 'APPROVED',
        description: 'Label for an approved task (in upper-case in supported language)',
    },
    tasksFeedRejectedLabel: {
        id: 'be.contentSidebar.activityFeed.taskNew.tasksFeedRejectedLabel',
        defaultMessage: 'Rejected',
        description: 'Label for a rejected task',
    },
    taskFeedRejectedUppercaseLabel: {
        id: 'be.contentSidebar.activityFeed.taskNew.tasksFeedRejectedUppercaseLabel',
        defaultMessage: 'REJECTED',
        description: 'Label for a task rejected (in upper-case in supported language)',
    },
    tasksFeedInProgressLabel: {
        id: 'be.contentSidebar.activityFeed.taskNew.tasksFeedInProgressLabel',
        defaultMessage: 'In Progress',
        description: 'Label for a task in progress',
    },
    taskFeedInProgressUppercaseLabel: {
        id: 'be.contentSidebar.activityFeed.taskNew.tasksFeedInProgressUppercaseLabel',
        defaultMessage: 'IN PROGRESS',
        description: 'Label for a task in progress (in upper-case in supported language)',
    },
    tasksFeedHeadlineApprovalCurrentUser: {
        id: 'be.contentSidebar.activityFeed.taskNew.tasksFeedHeadlineApprovalCurrentUser',
        defaultMessage: '{user} assigned you an Approval Task',
        description: 'Comment headline for an approval task assigned to the current user',
    },
    tasksFeedHeadlineApproval: {
        id: 'be.contentSidebar.activityFeed.taskNew.tasksFeedHeadlineApproval',
        defaultMessage: '{user} assigned an Approval Task',
        description: 'Comment headline for an approval task',
    },
    tasksFeedHeadlineGeneralCurrentUser: {
        id: 'be.contentSidebar.activityFeed.taskNew.tasksFeedHeadlineGeneralCurrentUser',
        defaultMessage: '{user} assigned you a Task',
        description: 'Comment headline for a general task assigned to the current user',
    },
    tasksFeedHeadlineGeneral: {
        id: 'be.contentSidebar.activityFeed.taskNew.tasksFeedHeadlineGeneral',
        defaultMessage: '{user} assigned a Task',
        description: 'Comment headline for a general task',
    },
    tasksFeedMoreAssigneesLabel: {
        id: 'be.contentSidebar.activityFeed.taskNew.tasksFeedMoreAssigneesLabel',
        defaultMessage: 'See all assignees',
        description: 'Label for button to expand flyout to see all task assignees',
    },
    tasksFeedAssigneeListTitle: {
        id: 'be.contentSidebar.activityFeed.taskNew.tasksFeedAssigneeListTitle',
        defaultMessage: 'Assignees',
        description: 'Title for list of all task assignees',
    },
    tasksFeedStatusRejected: {
        id: 'be.contentSidebar.activityFeed.taskNew.tasksFeedStatusRejected',
        defaultMessage: 'Rejected {dateTime}',
        description: 'Rejected task status, where dateTime is a readable time like "Today at 2pm"',
    },
    tasksFeedStatusApproved: {
        id: 'be.contentSidebar.activityFeed.taskNew.tasksFeedStatusApproved',
        defaultMessage: 'Approved {dateTime}',
        description: 'Approved task status, where dateTime is a readable time like "Today at 2pm"',
    },
    tasksFeedStatusCompleted: {
        id: 'be.contentSidebar.activityFeed.taskNew.tasksFeedStatusCompleted',
        defaultMessage: 'Completed {dateTime}',
        description: 'Completed task status, where dateTime is a readable time like "Today at 2pm"',
    },
    taskFeedStatusDue: {
        id: 'be.contentSidebar.activityFeed.taskNew.taskFeedStatusDue',
        defaultMessage: 'DUE {dateTime}',
        description:
            'Text for due date description formatted with relative date and relative time. (Upper-case in supported languages)',
    },
    taskAssignmentCompleted: {
        id: 'be.contentSidebar.activityFeed.taskNew.taskAssignmentCompleted',
        defaultMessage: 'Completed',
        description: 'Title for checkmark icon indicating someone completed a task',
    },
    taskDeleteMenuItem: {
        id: 'be.contentSidebar.activityFeed.task.taskDeleteMenuItem',
        defaultMessage: 'Delete task',
        description: 'Text to show on menu item to delete task',
    },
    taskEditMenuItem: {
        id: 'be.contentSidebar.activityFeed.task.taskEditMenuItem',
        defaultMessage: 'Modify task',
        description: 'Text to show on menu item to edit task',
    },
    taskDeletePrompt: {
        id: 'be.contentSidebar.activityFeed.task.taskDeletePrompt',
        defaultMessage: 'Are you sure you want to permanently delete this task?',
        description: 'Confirmation prompt text to delete task',
    },
    taskAnyAffordanceTooltip: {
        id: 'be.contentSidebar.activityFeed.task.taskAnyAffordanceTooltip',
        defaultMessage: 'Only one assignee is required to complete this task',
        description: 'Tooltip text for any task icon, explaining that the task only needs one assignee to complete.',
    },
    taskMultipleFilesAffordanceTooltip: {
        id: 'be.contentSidebar.activityFeed.task.taskMultipleFilesAffordanceTooltip',
        defaultMessage: 'There are multiple files associated with this task',
        description: 'Tooltip text for multi-file icon, explaining that the task involves multiple files',
    },
});

export default messages;
